import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Switch, message, Divider, Row, Col, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { fornecedorService } from '../services/api';

const FornecedoresList = () => {
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchFornecedores();
  }, []);

  const fetchFornecedores = async () => {
    setLoading(true);
    try {
      const response = await fornecedorService.list();
      setFornecedores(response.data);
    } catch (error) {
      message.error('Erro ao carregar fornecedores');
      console.error(error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir este fornecedor?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await fornecedorService.delete(id);
          message.success('Fornecedor excluído com sucesso');
          fetchFornecedores();
        } catch (error) {
          message.error('Erro ao excluir fornecedor');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingId) {
        await fornecedorService.update(editingId, values);
        message.success('Fornecedor atualizado com sucesso');
      } else {
        await fornecedorService.create(values);
        message.success('Fornecedor criado com sucesso');
      }
      setIsModalVisible(false);
      fetchFornecedores();
    } catch (error) {
      message.error('Erro ao salvar fornecedor');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
    },
    {
      title: 'Nome/Razão Social',
      dataIndex: 'nome_razao',
      key: 'nome_razao',
    },
    {
      title: 'Nome Fantasia',
      dataIndex: 'nome_fantasia',
      key: 'nome_fantasia',
    },
    {
      title: 'CPF/CNPJ',
      dataIndex: 'cpf_cnpj',
      key: 'cpf_cnpj',
      width: 150,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_pessoa',
      key: 'tipo_pessoa',
      width: 100,
      render: (tipo) => tipo === 'FISICA' ? 'Física' : 'Jurídica',
    },
    {
      title: 'Contato',
      key: 'contato',
      render: (_, record) => (
        <div>
          {record.telefone && <div>{record.telefone}</div>}
          {record.celular && <div>{record.celular}</div>}
          {record.email && <div>{record.email}</div>}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'ativo',
      key: 'ativo',
      width: 80,
      render: (ativo) => (
        <Tag color={ativo ? 'green' : 'red'}>
          {ativo ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Novo Fornecedor
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={fornecedores}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingId ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ ativo: true, tipo_pessoa: 'FISICA' }}
        >
          <Divider>Dados Básicos</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="nome_razao"
                label="Nome/Razão Social"
                rules={[{ required: true, message: 'Campo obrigatório' }]}
              >
                <Input placeholder="Nome completo ou razão social" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="nome_fantasia" label="Nome Fantasia">
                <Input placeholder="Nome fantasia (opcional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="tipo_pessoa"
                label="Tipo de Pessoa"
                rules={[{ required: true, message: 'Campo obrigatório' }]}
              >
                <Select>
                  <Select.Option value="FISICA">Pessoa Física</Select.Option>
                  <Select.Option value="JURIDICA">Pessoa Jurídica</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="cpf_cnpj" label="CPF/CNPJ">
                <Input placeholder="000.000.000-00" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="ativo" label="Status" valuePropName="checked">
                <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Contato</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="telefone" label="Telefone">
                <Input placeholder="(00) 0000-0000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="celular" label="Celular">
                <Input placeholder="(00) 00000-0000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                <Input placeholder="email@exemplo.com" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Endereço</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item name="cep" label="CEP">
                <Input placeholder="00000-000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="endereco" label="Endereço">
                <Input placeholder="Rua, Avenida..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="numero" label="Número">
                <Input placeholder="123" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="complemento" label="Complemento">
                <Input placeholder="Apto, Sala..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="bairro" label="Bairro">
                <Input placeholder="Bairro" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="cidade" label="Cidade">
                <Input placeholder="Cidade" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={2}>
              <Form.Item name="estado" label="UF">
                <Input placeholder="SP" maxLength={2} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Dados Bancários</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="banco" label="Banco">
                <Input placeholder="Nome do banco" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="agencia" label="Agência">
                <Input placeholder="0000" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="conta" label="Conta">
                <Input placeholder="00000-0" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="pix" label="Chave PIX">
            <Input placeholder="CPF, CNPJ, Email, Celular ou Chave Aleatória" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FornecedoresList;
